/**
 * /architect/dispatch — Create a new Architect task.
 *
 * Single-column form: description (multi-line markdown), scope dropdown,
 * autonomy dropdown, dry-run checkbox, submit. On success → redirect to
 * /architect/run/:runId.
 *
 * The dry-run toggle gives founders + screencast demos a way to walk
 * the graph without burning provider tokens.
 */
import type { ArchitectScope } from '@chaos/types/architect';
import type { AutonomyLevel } from '@chaos/types/kanban';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Loader2 } from 'lucide-react';
import * as React from 'react';

import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { Textarea } from '../../../components/ui/textarea';
import { architectApi } from '../../../lib/api/architect';

const SCOPE_OPTIONS: Array<{ value: ArchitectScope; label: string; hint: string }> = [
  { value: 'board', label: 'Board', hint: 'Decompose into cards on an existing board.' },
  { value: 'project', label: 'Project', hint: 'Decompose into a multi-board project plan.' },
  { value: 'card', label: 'Card', hint: 'Decompose a parent card into sub-cards.' },
];

const AUTONOMY_OPTIONS: Array<{ value: AutonomyLevel; label: string; hint: string }> = [
  { value: 'HITL', label: 'HITL', hint: 'Human-in-the-loop — approve every step.' },
  { value: 'HOTL', label: 'HOTL', hint: 'Human-on-the-loop — review milestones only.' },
  { value: 'HOOL', label: 'HOOL', hint: 'Human-out-of-the-loop — review final output.' },
  {
    value: 'agent_only',
    label: 'Agent only',
    hint: 'No human review; agents proceed autonomously.',
  },
];

function ArchitectDispatchPage(): React.JSX.Element {
  const navigate = useNavigate();
  const [description, setDescription] = React.useState('');
  const [scope, setScope] = React.useState<ArchitectScope>('board');
  const [autonomy, setAutonomy] = React.useState<AutonomyLevel>('HITL');
  const [dryRun, setDryRun] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const dispatchM = useMutation({
    mutationFn: () =>
      architectApi.dispatch({
        description: description.trim(),
        scope,
        autonomyLevel: autonomy,
        dryRun,
      }),
    onSuccess: async (ack) => {
      await navigate({ to: '/architect/run/$runId', params: { runId: ack.runId } });
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : 'Could not dispatch the task.');
    },
  });

  const submit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    setError(null);
    if (description.trim().length < 8) {
      setError('Describe the task in at least 8 characters.');
      return;
    }
    dispatchM.mutate();
  };

  const scopeHint = SCOPE_OPTIONS.find((o) => o.value === scope)?.hint ?? '';
  const autonomyHint = AUTONOMY_OPTIONS.find((o) => o.value === autonomy)?.hint ?? '';

  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="flex items-center justify-between gap-4 px-6 pt-3 pb-2">
        <h1 className="truncate text-[15px] font-semibold tracking-tight text-foreground">
          New task
        </h1>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-10 pt-6">
        <form onSubmit={submit} className="mx-auto flex w-full max-w-2xl flex-col gap-5">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="architect-description">Description</Label>
            <Textarea
              id="architect-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Decompose: build a simple TODO API with auth and unit tests."
              className="min-h-[160px]"
              autoFocus
            />
            <p className="text-[11px] text-muted-foreground/80">
              Markdown is preserved. The agent walks intake → classify → decompose → routing →
              dispatch.
            </p>
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <Label>Scope</Label>
              <Select value={scope} onValueChange={(v) => setScope(v as ArchitectScope)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SCOPE_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground/80">{scopeHint}</p>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label>Autonomy</Label>
              <Select value={autonomy} onValueChange={(v) => setAutonomy(v as AutonomyLevel)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {AUTONOMY_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground/80">{autonomyHint}</p>
            </div>
          </div>

          <label className="flex items-start gap-2 text-[13px] text-foreground">
            <input
              type="checkbox"
              checked={dryRun}
              onChange={(e) => setDryRun(e.target.checked)}
              className="mt-0.5 h-3.5 w-3.5"
            />
            <span className="flex flex-col">
              <span>Dry-run</span>
              <span className="text-[11px] text-muted-foreground/80">
                Walk the graph without calling the LLM. Useful for demos and tests.
              </span>
            </span>
          </label>

          {error && (
            <p
              role="alert"
              className="rounded border border-error/40 bg-error/10 px-3 py-2 text-[12px] text-error"
            >
              {error}
            </p>
          )}

          <div className="flex items-center gap-3">
            <Button type="submit" disabled={dispatchM.isPending} size="sm">
              {dispatchM.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />}
              Dispatch
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => navigate({ to: '/architect' })}
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export const Route = createFileRoute('/_authenticated/architect/dispatch')({
  component: ArchitectDispatchPage,
});
